import React, { useEffect, useState } from "react";
import {
  FiSearch,
  FiPlus,
  FiEye,
} from "react-icons/fi";
import { useGetInterviewsQuery } from "../redux/api/interview.api";
import { useNavigate, useSearchParams } from "react-router-dom";
import Pagination from "../components/interview/pagination";
import { GoogleInFeedAd } from "../components/ads/GoogleInFeedAd";
import { PlayIcon } from "lucide-react";

const useDebouncedSearch = (initialValue: string, delay: number = 300) => {
  const [search, setSearch] = useState(initialValue);
  const [debouncedSearch, setDebouncedSearch] = useState(initialValue);

  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [search, delay]);

  return { search, debouncedSearch, setSearch };
}

export default function InterviewedPositions() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams()
  const { search, setSearch, debouncedSearch } = useDebouncedSearch(searchParams.get('search') ?? "", 300);
  const { data: interviewsData, isLoading } = useGetInterviewsQuery({
    pageIndex: +(searchParams.get('pageIndex') || 0),
    search: debouncedSearch
  }, {
    refetchOnMountOrArgChange: true,
  });


  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.set('pageIndex', currentPage.toString());
    params.set('search', search)
    setSearchParams(params);
  }, [currentPage, search, searchParams, setSearchParams]);
  const totalCount = React.useMemo(() => {
    return interviewsData?.totalCount || 0
  }, [interviewsData])

  const colors = React.useMemo(() => {
    return Array.from({ length: totalCount * 2 }, () => getRandomBoldColor());
  }, [totalCount]);

  const filteredInterviews = React.useMemo(() => {
    return interviewsData?.jobInterviews?.filter((item) =>
      item.jobTitle?.toLowerCase().includes(debouncedSearch?.toLowerCase()) ||
      item.jobRole?.toLowerCase().includes(debouncedSearch?.toLowerCase()) ||
      item.companyName?.toLowerCase().includes(debouncedSearch?.toLowerCase())
    ) || [];
  }, [interviewsData]);

  if (isLoading) {
    return (
      <div className="flex w-full items-center overflow-hidden justify-center min-w-max min-h-[60vh] bg-white dark:bg-[#232226]">
        <div className="w-12 h-12 border-4 border-t-transparent border-accent-500 rounded-full animate-spin"></div>
      </div>
    )
  }
  return (
    <div className={'w-full'}>
      {
        <div className=" bg-gray-100 dark:bg-[#242424] text-gray-900 dark:text-gray-100 transition-colors duration-300 p-6 flex flex-col">
          {/* Header */}
          <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
            <h1 className="text-xl font-bold">Interviewed Positions</h1>

            <div className="flex flex-1 justify-end gap-2 items-center">
              {/* Search */}
              <div className="flex min-w-[10rem] max-w-[27rem] items-center bg-white dark:bg-[#111] px-3 py-2 rounded-lg shadow-sm w-full ">
                <FiSearch className="text-gray-500 dark:text-gray-400" />
                <input
                  type="text"
                  placeholder="Search positions"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="bg-transparent outline-none px-2 w-full placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              {/* New Interview Button */}
              <button
                onClick={() => navigate('/interview-setup?newPosition=true')}
                className="flex items-center gap-1 bg-accent-500 hover:bg-accent-600 text-white px-4 py-2 rounded-lg  md:w-auto"
              >
                <FiPlus /> New <span className="hidden sm_mb:inline">Interview</span>
              </button>

            </div>
          </div>

          {/* Subtitle */}
          <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
            Showing results {filteredInterviews.length} of {totalCount}
          </p>

          {/* Cards */}
          <div className="flex-1 text-sm overflow-auto custom-scrollbar !max-h-60-vh-safe tablet:text-base space-y-4 min-h-0">


            {

              // No Interview list fallback
              !filteredInterviews.length
                ? (
                  <div className="flex flex-col items-center justify-center min-h-[30vh] text-gray-500 dark:text-gray-400">
                    <FiSearch className="text-3xl mb-2" />
                    <p>No interviewed positions found.</p>
                  </div>
                )
                : filteredInterviews.map((item, index) => (
                  <>
                    <div
                      key={item.jobId}
                      className="bg-white text-sm   dark:bg-[#212121] rounded-lg shadow-md p-4 grid grid-cols-1 small:grid-cols-[63%_33%]  lg:grid-cols-[40%_28%_28%] xl:grid-cols-[38%_19%_19%_19%] gap-4 lg:items-center border border-gray-200 dark:border-gray-700"
                    >
                      {/* Left Info */}
                      <div className="flex gap-4 items-start sm:items-center flex-1 min-h-0">
                        <div
                          style={{ backgroundColor: colors[index] }}
                          className={`w-12 h-12 rounded-full flex items-center text-white justify-center flex-shrink-0`}>
                          <span className="text-3xl">{item.jobTitle[0]}</span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold truncate">
                            Position: {item.jobTitle || "N/A"}
                          </p>
                          <p className="text-sm truncate">Role: {item.jobRole || "N/A"}</p>
                          <p className="text-sm truncate">
                          Created: {item.createdDate ? new Date(item.createdDate).toDateString() : "N/A"}
                          </p>
                        </div>
                      </div>

                      {/* Middle Info */}
                      <div className="flex flex-col md:flex-wrap md:items-start md:gap-x-8 md:gap-y-2 flex-1 min-h-0 lg:col-span-2 xl:col-span-2">
                        <div className="flex flex-small small:flex-col items-start justify-between gap-5 mobile:gap-0">
                          <span className="flex flex-col items-start text-sm lg:items-start xs:items-start gap-1">
                            <span>
                              Company: <span className="font-semibold">{item.companyName || "N/A"}</span>
                            </span>    
                          </span>
                          <div className="flex flex-col">
                            <p>Grade: {item.jobGrade || "N/A"}</p>
                            <p>Salary: {item.salaryMax || "N/A"}</p>
                            <p>Completed Interviews: {item.totalInterviews}</p>
                          </div>
                        </div>
                      </div>
 
                      {/* Actions */}
                      <div className="flex flex-wrap self-start small:col-span-2 lg:col-span-3 xl:col-span-1 relative gap-2 items-center justify-between md:justify-end flex-shrink-0">
                        <div className="flex flex-wrap self-start  relative gap-2 items-center justify-start md:justify-end flex-shrink-0">
                          <button
                            onClick={() => navigate(`/interview-setup?jobId=${item.jobId}`)}
                            className="bg-accent-200 flex items-center gap-1 dark:bg-accent-600 hover:bg-accent-300 dark:hover:bg-accent-500 text-accent-900 dark:text-white px-3 py-1 rounded-lg"
                          >
                            <PlayIcon className="w-5 h-5" /> <span>Re-Interview </span>
                          </button>
                          {
                            item.totalInterviews > 0 ? 
                            (
                            <button
                            onClick={() => {
                              navigate(`/interviews/${item.jobId}?jobTitle=${item.jobTitle}&seniority=${item.seniorityLevel || ''}&pageIndex=0`);
                            }}
                            className="bg-purple-200 flex items-center gap-1 dark:bg-purple-600 hover:bg-purple-300 dark:hover:bg-purple-500 text-purple-900 dark:text-white px-3 py-1 rounded-lg"
                             >
                            <FiEye className="w-5 h-5" /><span> ({item.totalInterviews}) Scores</span>
                          </button>
                            ) :
                            (
                          <button disabled
                            className="bg-gray-200 flex items-center gap-1 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-white-900 dark:text-white px-3 py-1 rounded-lg"
                             >
                            <FiEye className="w-5 h-5" /><span> No Interview</span>
                          </button>
                            )
                          }
                          
                        </div>
                        {/* <button className="bg-blue-200 dark:bg-blue-600 hover:bg-blue-300 dark:hover:bg-blue-500 text-blue-900 dark:text-white p-2 rounded-lg">
                      <FiEdit2 />
                    </button>
                    <button className="bg-red-200 dark:bg-red-600 hover:bg-red-300 dark:hover:bg-red-500 text-red-900 dark:text-white p-2 rounded-lg">
                      <FiTrash2 />
                    </button> */}
                        
                      </div>
                    </div>
                    {((index + 1) % 5 == 0 || index == 0) && (
                      <GoogleInFeedAd />
                    )}
                  </>
                ))}
          </div>

          {/* Pagination */}
          <div className="flex justify-center items-center gap-2 mt-3 flex-wrap">
            {filteredInterviews.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalItems={totalCount}
                perPage={10}
                totalPages={Math.ceil(totalCount / 10)}
                onPageChange={setCurrentPage}
              />
            )}
          </div>
        </div>
      }
    </div>
  );
}

export function getRandomBoldColor(): string {
  const hue = Math.floor(Math.random() * 360);    // full color wheel
  const saturation = 70 + Math.random() * 30;     // 70%–100%
  const lightness = 40 + Math.random() * 20;      // 40%–60%
  return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
}

